(function () {
  'use strict';

  function createProductState(data) {
    const colourIndex = data.options.findIndex((name) => /^(color|colour)$/i.test(name));
    const sizeIndex = data.options.findIndex((name) => name.toLowerCase() === 'size');
    const linked = data.variants.find((variant) => String(variant.id) === String(data.initialVariantId));
    const first = linked || data.variants.find((variant) => colourIndex >= 0 && variant.options[colourIndex] === 'Green') || data.variants[0];
    let selected = first ? [...first.options] : data.options.map(() => null);
    if (!linked && !data.simple && sizeIndex >= 0) {
      const sizes = [...new Set(data.variants.map((variant) => variant.options[sizeIndex]))];
      selected[sizeIndex] = sizes.length === 1 && sizes[0] === 'One Size' ? 'One Size' : null;
    }
    function matching(options) {
      return data.variants.filter((variant) => variant.options.every((value, index) => options[index] == null || options[index] === value));
    }
    function state() {
      const matches = matching(selected);
      const variant = selected.every((value) => value != null) ? matches[0] || null : null;
      const representative = variant || matches[0] || null;
      let status = variant ? (variant.available ? 'available' : 'sold-out') : 'choose-size';
      if (representative && data.hideZeroPrices !== false && Number(representative.price) === 0) status = 'price-pending';
      return { selected: [...selected], variant, representative, status, colour: colourIndex >= 0 ? selected[colourIndex] : '', sizeIndex };
    }
    function choice(index, value) {
      // Colour choices remain reachable even when the previous size is not offered.
      const options = [...selected];
      options[index] = value;
      if (index === colourIndex && sizeIndex >= 0) options[sizeIndex] = null;
      const matches = matching(options);
      return { offered: matches.length > 0, soldOut: matches.length > 0 && matches.every((variant) => !variant.available) };
    }
    function select(index, value) {
      if (!choice(index, value).offered) return state();
      selected[index] = value;
      if (index === colourIndex && sizeIndex >= 0 && matching(selected).length === 0) selected[sizeIndex] = null;
      return state();
    }
    function updateVariants(variants) {
      data.variants.forEach((variant) => {
        const fresh = variants.find((candidate) => candidate.id === variant.id);
        if (fresh) variant.available = fresh.available;
      });
      return state();
    }
    return { state, choice, select, updateVariants };
  }

  function normaliseColourText(value) {
    return String(value || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  }

  function coloursInText(text, colours) {
    let remaining = ` ${normaliseColourText(text)} `;
    // Match full colour names before shorter names such as Blue.
    return [...colours].sort((a, b) => b.length - a.length).filter((colour) => {
      const phrase = ` ${normaliseColourText(colour)} `;
      if (!remaining.includes(phrase)) { return false; }
      remaining = remaining.split(phrase).join(' ');
      return true;
    });
  }

  function galleryFor(data, state) {
    const colourIndex = data.options.findIndex((name) => /^(color|colour)$/i.test(name));
    const hasColourImages = colourIndex >= 0 && data.variants.some((variant) => variant.image);
    const featured = state.representative?.image || (hasColourImages && data.variants.find((variant) => variant.options[colourIndex] === state.colour && variant.image)?.image);
    if (hasColourImages && state.colour && !featured) return [];
    if (data.images.length) {
      const colours = colourIndex >= 0 ? [...new Set(data.variants.map((variant) => variant.options[colourIndex]))] : [];
      const images = data.images.filter((image) => {
        if (!state.colour || colours.length < 2) { return true; }
        const assigned = data.variants.filter((variant) => variant.image === image.src).map((variant) => variant.options[colourIndex]);
        const described = assigned.length ? assigned : coloursInText(image.alt, colours);
        const matches = described.length ? described : coloursInText(image.src.split('?')[0].split('/').pop(), colours);
        // Unlabelled images remain shared, for example a garment care guide.
        return matches.length === 0 || matches.includes(state.colour);
      });
      if (featured) {
        const existing = images.findIndex((image) => image.src === featured);
        if (existing >= 0) images.unshift(...images.splice(existing, 1));
        else images.unshift({ src: featured, alt: data.title, kind: 'real' });
      }
      return images;
    }
    const fallback = data.fallbacks.find((image) => image.colour === state.colour) || (state.colour === '' ? data.fallbacks[0] : null);
    if (!fallback?.src) return [];
    const studio = { ...fallback, alt: `${data.title}${state.colour ? `, ${state.colour}` : ''}` };
    return fallback.worn && fallback.kind === 'illustrative' ? [{ src: fallback.worn, alt: 'Illustrative green jersey worn by a cyclist', kind: 'illustrative' }, studio] : [studio];
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createProductState, galleryFor };
  if (typeof document === 'undefined') return;

  function initProduct(root) {
    if (root.dataset.productReady) return;
    const json = root.querySelector('[data-product-json]');
    if (!json) return;
    let data;
    try { data = JSON.parse(json.textContent); } catch (_) { return; }
    const model = createProductState(data);
    const form = root.querySelector('[data-product-form]');
    const variantInput = form.elements.id;
    const submit = root.querySelector('[data-product-submit]');
    const status = root.querySelector('[data-product-status]');
    const error = root.querySelector('[data-product-error]');
    let adding = false;
    let galleryKey = '';
    let changedColour = false;
    let unresolvedAddition = false;

    function renderGallery(state) {
      const images = galleryFor(data, state);
      const key = images.map((image) => image.src).join('|');
      if (key === galleryKey) return;
      galleryKey = key;
      const media = root.querySelector('[data-product-media]');
      const thumbnails = root.querySelector('[data-product-thumbnails]');
      media.replaceChildren();
      thumbnails.replaceChildren();
      root.querySelector('[data-image-note]').hidden = !images.some((image) => image.kind === 'illustrative');
      if (!images.length) {
        const empty = document.createElement('div');
        empty.className = 'cc-product__no-image';
        empty.textContent = 'Product photography coming soon';
        media.append(empty);
      }
      images.forEach((image, index) => {
        const link = document.createElement('a');
        link.className = 'cc-product__image-link';
        link.href = image.src;
        link.target = '_blank';
        link.rel = 'noopener';
        link.setAttribute('aria-label', `Enlarge image ${index + 1} of ${data.title}`);
        const img = document.createElement('img');
        img.src = image.src;
        img.alt = image.alt;
        img.width = image.width || 1150;
        img.height = image.height || 1368;
        img.loading = index === 0 ? 'eager' : 'lazy';
        link.append(img);
        media.append(link);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cc-product__thumbnail';
        button.setAttribute('aria-label', `Show image ${index + 1} of ${images.length}`);
        button.setAttribute('aria-pressed', String(index === 0));
        const thumbnail = img.cloneNode();
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        button.append(thumbnail);
        button.addEventListener('click', () => {
          media.prepend(link);
          media.querySelectorAll('.cc-product__image-link').forEach((item, position) => { item.hidden = position > 0; });
          thumbnails.querySelectorAll('button').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        });
        thumbnails.append(button);
        link.hidden = index > 0;
      });
      thumbnails.hidden = images.length < 2;
    }

    function render(updateUrl = false) {
      const current = model.state();
      root.querySelectorAll('[data-option-input]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const choice = model.choice(index, input.value);
        input.checked = current.selected[index] === input.value;
        input.disabled = adding || !choice.offered;
        const label = input.closest('[data-option-choice]');
        const unavailable = !choice.offered || choice.soldOut;
        label.dataset.soldOut = String(unavailable);
        label.querySelector('[data-choice-state]').textContent = unavailable ? data.outOfStock : '';
      });
      root.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelector('[data-option-current]').textContent = /^(color|colour)$/i.test(data.options[index]) && current.selected[index] ? `: ${current.selected[index]}` : '';
      });
      variantInput.value = current.variant ? String(current.variant.id) : '';
      const price = current.representative;
      const badgeVariants = data.variants.filter((variant) => variant.options.every((value, index) => current.selected[index] == null || current.selected[index] === value));
      root.querySelector('[data-product-badges]')?.update?.(badgeVariants);
      root.querySelector('[data-product-price]').textContent = current.status === 'price-pending' ? 'Price coming soon' : price?.formattedPrice || 'Choose your options';
      const compare = root.querySelector('[data-product-compare]');
      compare.hidden = !price || current.status === 'price-pending' || !(price.comparePrice > price.price);
      compare.textContent = price?.formattedComparePrice || '';
      const labels = { available: 'Add to cart', 'sold-out': 'Sold out', 'choose-size': 'Choose a size', 'price-pending': 'Price coming soon' };
      const messages = { available: '', 'sold-out': current.sizeIndex >= 0 ? 'Sold out in this size.' : 'This item is sold out.', 'choose-size': changedColour ? 'Choose a size for this colour.' : 'Choose a size.', 'price-pending': 'This item will be available to buy once its price is confirmed.' };
      submit.disabled = adding || unresolvedAddition || current.status !== 'available';
      submit.textContent = adding ? 'Adding…' : labels[current.status];
      status.textContent = messages[current.status];
      form.setAttribute('aria-busy', String(adding));
      renderGallery(current);
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (current.variant) url.searchParams.set('variant', current.variant.id);
        else url.searchParams.delete('variant');
        history.replaceState({}, '', url);
      }
    }

    root.querySelectorAll('[data-option-input]').forEach((input) => input.addEventListener('change', () => {
      const index = Number(input.dataset.optionIndex);
      changedColour = /^(color|colour)$/i.test(data.options[index]);
      model.select(index, input.value);
      error.hidden = true;
      render(true);
    }));
    form.addEventListener('submit', async (event) => {
      if (!window.LLCC?.cart) return;
      event.preventDefault();
      if (adding || model.state().status !== 'available' || !form.reportValidity()) return;
      const payload = new FormData(form);
      adding = true;
      error.hidden = true;
      render();
      try {
        await window.LLCC.cart.add(payload, { opener: submit });
      } catch (failure) {
        unresolvedAddition = Boolean(failure.uncertain || failure.committed);
        if (failure.code === 'inventory') {
          try {
            const response = await fetch(`${data.url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
            if (response.ok) model.updateVariants((await response.json()).variants);
          } catch (_) { /* Keep the last known options available for correction. */ }
        }
        error.querySelector('[data-product-error-text]').textContent = failure.code === 'inventory' ? 'This item is unavailable. Please choose another option.' : failure.committed ? 'Your item was added, but the cart could not be displayed. View your cart to continue.' : failure.uncertain ? 'We couldn’t confirm whether this item was added. Check your cart before adding it again.' : 'We couldn’t add this item to your cart. Please try again.';
        error.querySelector('[data-product-retry]').hidden = Boolean(failure.uncertain || failure.committed || failure.code === 'inventory');
        error.querySelector('[data-product-view-cart]').hidden = !(failure.uncertain || failure.committed);
        error.hidden = false;
      } finally { adding = false; render(); }
    });
    root.querySelector('[data-product-retry]').addEventListener('click', () => form.requestSubmit());
    const guide = root.querySelector('[data-size-guide]');
    let guideOpener;
    root.querySelectorAll('[data-size-guide-open]').forEach((button) => button.addEventListener('click', () => {
      guideOpener = button;
      document.dispatchEvent(new CustomEvent('llcc:overlay:opening', { detail: { id: `size-guide-${data.id}` } }));
      guide.showModal();
      guide.querySelector('[data-size-guide-close]').focus();
    }));
    guide.querySelector('[data-size-guide-close]').addEventListener('click', () => guide.close());
    guide.addEventListener('close', () => guideOpener?.focus());
    document.addEventListener('llcc:cart:updated', () => {
      if (!unresolvedAddition || adding) return;
      unresolvedAddition = false;
      error.hidden = true;
      render();
    });
    root.querySelectorAll('.cc-product__details > details').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!detail.open || !detail.isConnected) return;
          const top = detail.querySelector('summary').getBoundingClientRect().top;
          if (top < 16) window.scrollBy({ top: top - 16, behavior: 'instant' });
        }));
      });
    });
    if (root.hasAttribute('data-gift-card')) {
      const media = root.querySelector('[data-product-media]');
      const motion = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
      let frame = 0;
      let position;
      let activeCard;
      const reset = () => {
        cancelAnimationFrame(frame);
        frame = 0;
        if (!activeCard) return;
        activeCard.style.transform = '';
        activeCard.classList.remove('cc-gift-card--active');
        activeCard = null;
      };
      media.addEventListener('pointermove', (event) => {
        if (!motion.matches || event.pointerType !== 'mouse') return;
        position = { x: event.clientX, y: event.clientY };
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const card = media.querySelector('.cc-product__image-link');
          if (!card) return;
          if (activeCard && activeCard !== card) reset();
          activeCard = card;
          let shine = card.querySelector('.cc-gift-card__shine');
          if (!shine) {
            shine = document.createElement('span');
            shine.className = 'cc-gift-card__shine';
            shine.setAttribute('aria-hidden', 'true');
            card.appendChild(shine);
          }
          const rect = media.getBoundingClientRect();
          const x = Math.max(0, Math.min(1, (position.x - rect.left) / rect.width));
          const y = Math.max(0, Math.min(1, (position.y - rect.top) / rect.height));
          card.classList.add('cc-gift-card--active');
          card.style.transform = `perspective(1200px) rotateX(${((0.5 - y) * 8).toFixed(2)}deg) rotateY(${((x - 0.5) * 8).toFixed(2)}deg)`;
          shine.style.transform = `translate(${(10 - (x - 0.5) * 12).toFixed(1)}%, ${(-10 - (y - 0.5) * 12).toFixed(1)}%)`;
          shine.style.setProperty('--shine-strength', (0.65 + (x - 0.5) * 0.2 - (y - 0.5) * 0.2).toFixed(2));
        });
      });
      media.addEventListener('pointerleave', reset);
      media.addEventListener('pointercancel', reset);
      motion.addEventListener('change', reset);
    }
    root.dataset.productReady = 'true';
    root.querySelector('[data-native-variants]').hidden = true;
    variantInput.required = false;
    root.querySelector('[data-product-options]').hidden = false;
    render();
  }
  const init = (scope = document) => scope.querySelectorAll('[data-product-root]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
